import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardMedia,
  Rating,
  Chip,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Wifi,
  AcUnit,
  Tv,
  Bathtub,
  LocalParking,
  Kitchen,
  Balcony,
  RoomService,
  BusinessCenter,
  FamilyRestroom,
  CoffeeMaker,
  LocalLaundryService,
  Pool,
  FitnessCenter,
  Restaurant,
} from '@mui/icons-material';
import TopBar from './TopBar';
import NavigationBar from './NavigationBar';
import Footer from './Footer';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_CODE = process.env.REACT_APP_HOTEL_CODE;

const RoomDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const loadRoom = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/hotel/rooms/get.php`, {
          params: { id, hotel_code: HOTEL_CODE }
        });
        
        if (response.data.success) {
          setRoom(response.data.room);
        } else {
          setError(response.data.error || 'Room not found');
        }
      } catch (error) {
        console.error('Error loading room:', error);
        setError('Failed to load room details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadRoom();
  }, [id]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) return imagePath;
    
   
    
    const serverRoot = process.env.REACT_APP_SERVER_ROOT;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${serverRoot}${cleanPath}`;
  };

  const getAmenityIcon = (amenityId) => {
    const icons = {
      1: <Wifi />,
      2: <AcUnit />,
      3: <Tv />,
      4: <Bathtub />,
      5: <LocalParking />,
      6: <Pool />,
      7: <FitnessCenter />,
      8: <Restaurant />,
      9: <RoomService />,
      10: <BusinessCenter />,
      11: <FamilyRestroom />,
      12: <Balcony />,
      13: <Kitchen />,
      14: <CoffeeMaker />,
      15: <LocalLaundryService />,
    };
    return icons[amenityId] || <Wifi />;
  };

  const getAmenityName = (amenityId) => {
    const amenities = {
      1: 'Free WiFi',
      2: 'Air Conditioning',
      3: 'Smart TV',
      4: 'Private Bathroom',
      5: 'Parking',
      6: 'Swimming Pool',
      7: 'Fitness Center',
      8: 'Restaurant',
      9: 'Room Service',
      10: 'Business Center',
      11: 'Family Friendly',
      12: 'Balcony',
      13: 'Kitchen',
      14: 'Coffee Maker',
      15: 'Laundry Service',
    };
    return amenities[amenityId] || 'Amenity';
  };

  const handleBookRoom = () => {
    navigate('/booking', { state: { room } });
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <TopBar />
        <NavigationBar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading room details...
          </Typography>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error || !room) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <TopBar />
        <NavigationBar />
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Room not found'}
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
          >
            Back to Rooms
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  const fallbackImage = 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Top Bar */}
      <TopBar />

      {/* Navigation */}
      <NavigationBar />

      {/* Main Content */}
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          {/* Back Button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ mb: 3 }}
          >
            Back to Rooms
          </Button>

          <Grid container spacing={4}>
            {/* Room Images */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
                <CardMedia
                  component="img"
                  height="400"
                  image={room.images && room.images[selectedImage] ? getImageUrl(room.images[selectedImage]) : fallbackImage}
                  alt={room.name}
                  onError={(e) => {
                    e.target.src = fallbackImage;
                  }}
                />
              </Card>
              
              {/* Thumbnail Images */}
              {room.images && room.images.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                  {room.images.map((image, index) => (
                    <Card
                      key={index}
                      sx={{
                        minWidth: 100,
                        height: 80,
                        cursor: 'pointer',
                        border: selectedImage === index ? 2 : 0,
                        borderColor: 'primary.main'
                      }}
                      onClick={() => setSelectedImage(index)}
                    >
                      <CardMedia
                        component="img"
                        height="80"
                        image={getImageUrl(image)}
                        alt={`${room.name} ${index + 1}`}
                        onError={(e) => {
                          e.target.src = fallbackImage;
                        }}
                      />
                    </Card>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Room Details */}
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 4, borderRadius: 3, position: 'sticky', top: 20 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  {room.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Rating value={parseFloat(room.rating) || 4.5} readOnly />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {parseFloat(room.rating) || 4.5}/5
                  </Typography>
                </Box>

                <Typography variant="h3" color="primary" gutterBottom fontWeight="bold">
                  ₦{room.price}
                  <Typography variant="body1" color="text.secondary" component="span">
                    /night
                  </Typography>
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Chip label={room.type} sx={{ mr: 1, mb: 1 }} color="primary" />
                  <Chip label={`${room.capacity} Guests`} sx={{ mr: 1, mb: 1 }} />
                  {room.size && <Chip label={`${room.size} m²`} sx={{ mr: 1, mb: 1 }} />}
                  <Chip 
                    label={room.status || 'Available'} 
                    color={room.status === 'Available' ? 'success' : 'warning'}
                    sx={{ mb: 1 }}
                  />
                </Box>

                <Typography variant="body1" paragraph>
                  {room.description || 'Experience luxury and comfort in our beautifully appointed room. Featuring premium amenities and stunning views, this room is designed for your ultimate relaxation.'}
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleBookRoom}
                  disabled={room.status !== 'Available'}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold', mb: 2 }}
                >
                  {room.status === 'Available' ? 'Book This Room' : 'Not Available'}
                </Button>

                {room.status !== 'Available' && (
                  <Typography variant="body2" color="error" textAlign="center">
                    This room is currently {room.status?.toLowerCase()}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Amenities Section */}
          {room.amenities && room.amenities.length > 0 && (
            <Paper sx={{ p: 4, borderRadius: 3, mt: 4 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Room Amenities
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {room.amenities.map((amenityId) => (
                  <Grid item xs={6} sm={4} md={3} key={amenityId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                      <IconButton color="primary" size="small">
                        {getAmenityIcon(amenityId)}
                      </IconButton>
                      <Typography variant="body2">
                        {getAmenityName(amenityId)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Room Features */}
          <Paper sx={{ p: 4, borderRadius: 3, mt: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Room Features
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2" color="text.secondary">Room Type:</Typography>
                  <Typography variant="body2" fontWeight="medium">{room.type}</Typography>
                </Box>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2" color="text.secondary">Capacity:</Typography>
                  <Typography variant="body2" fontWeight="medium">{room.capacity} guests</Typography>
                </Box>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2" color="text.secondary">Room Size:</Typography>
                  <Typography variant="body2" fontWeight="medium">{room.size || 'N/A'} m²</Typography>
                </Box>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2" color="text.secondary">Rating:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={parseFloat(room.rating) || 4.5} readOnly size="small" />
                    <Typography variant="body2">({parseFloat(room.rating) || 4.5})</Typography>
                  </Box>
                </Box>
                <Divider />
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default RoomDetails;